
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Check, X, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

interface StandardOption {
  id: string;
  title: string;
  category: string;
  status: string;
  identifier?: string;
  version?: string;
  scope?: string;
  purpose?: string;
  requirements?: {
    id: string;
    identifier: string;
    text: string;
    type?: string;
    level?: string;
    section?: string;
    subsection?: string;
    requirement_text?: string;
  }[];
}

interface StandardsSelectorProps {
  onSelectStandard: (standard: StandardOption) => void;
  onCancel: () => void;
  modalTitle?: string;
}

export function StandardsSelector({ 
  onSelectStandard, 
  onCancel, 
  modalTitle = "Select a Standard" 
}: StandardsSelectorProps) {
  const [standards, setStandards] = useState<StandardOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchAvailableStandards();
  }, []);

  const fetchAvailableStandards = async () => {
    try {
      setIsLoading(true);
      
      // Fetch standards with basic information
      const { data: standardsData, error: standardsError } = await supabase
        .from('standards')
        .select(`
          id, 
          title, 
          category, 
          status, 
          identifier, 
          version, 
          scope, 
          purpose,
          requirements (
            id,
            identifier,
            text,
            requirement_type,
            level,
            section,
            subsection,
            requirement_text
          )
        `);
      
      if (standardsError) {
        throw standardsError;
      }
      
      if (standardsData) {
        // Transform the data to match our interface
        const transformedStandards: StandardOption[] = standardsData.map(standard => ({
          id: standard.id,
          title: standard.title,
          category: standard.category,
          status: standard.status,
          identifier: standard.identifier,
          version: standard.version,
          scope: standard.scope,
          purpose: standard.purpose,
          requirements: standard.requirements?.map(req => ({
            id: req.id,
            identifier: req.identifier || '',
            text: req.text || '',
            type: req.requirement_type,
            level: req.level,
            section: req.section,
            subsection: req.subsection,
            requirement_text: req.requirement_text
          }))
        }));
        
        setStandards(transformedStandards);
      }
    } catch (error: any) {
      console.error("Error fetching standards:", error);
      toast.error(error.message || "Failed to load standards");
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique categories for filtering
  const categories = Array.from(new Set(standards.map(std => std.category)));

  // Filter standards by selected category and search query
  const filteredStandards = standards
    .filter(std => !selectedCategory || std.category === selectedCategory)
    .filter(std => 
      searchQuery === "" || 
      std.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      std.category.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-2xl font-bold">{modalTitle}</h2>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Search input */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search standards..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        {/* Category filters */}
        <div className="px-6 py-2 overflow-x-auto">
          <div className="flex space-x-2">
            <Button 
              variant={selectedCategory === null ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory(null)}
            >
              All
            </Button>
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-grow">
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[300px]">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="ml-2 text-muted-foreground">Loading standards...</p>
            </div>
          ) : filteredStandards.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredStandards.map(standard => (
                <div 
                  key={standard.id} 
                  className="bg-card border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => onSelectStandard(standard)}
                >
                  <h3 className="font-semibold text-lg mb-2">{standard.title}</h3>
                  <div className="text-sm text-muted-foreground mb-3">{standard.category}</div>
                  <div className="flex justify-between items-center">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      standard.status === 'Active' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                    }`}>
                      {standard.status}
                    </span>
                    <div className="flex items-center gap-2">
                      {standard.requirements && standard.requirements.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {standard.requirements.length} requirements
                        </span>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="ml-auto"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Select
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No standards available for the selected category.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
